<script setup lang="ts">
import StudioSelectField from "../StudioSelectField.vue";
import { computed, nextTick, ref, watch } from "vue";
import StudioSidebarContent from "../StudioSidebarContent.vue";
import { toast } from "vue-sonner";
import { useStylePresets } from "../EntityPresetEditor/stylePresets";
const { options: questStyleOptions, error: questStyleError, retry: retryQuestStyles } = useStylePresets("questStyles");
import type { TreeSelectOption } from "naive-ui";
import QuestReferenceSelect from "./QuestReferenceSelect.vue";
import QuestMainAreaSelect from "./QuestMainAreaSelect.vue";
import QuestFlowGraph from "./QuestFlowGraph.vue";
import ClipPropertyEditor from "../DialogueEditor/components/clip-editors/ClipPropertyEditor.vue";
import type { ClipPropertyDefinition } from "../DialogueEditor/types/DialogueNode";
import { createQuestChapter, createQuestMain, createQuestSub, removeQuestSubQuests } from "./questProject";
import type { QuestMain, QuestProject, QuestSelection, QuestSub } from "./types";

const props = defineProps<{ project: QuestProject; sidebarDisabled?: boolean }>();
const selection = ref<QuestSelection | null>(null);
const editorView = ref<"properties" | "flow">("properties");
const search = ref("");
const inspectorSection = ref("basic");
const inspectorSections = [
  { key: "basic", label: "基本信息", hint: "标题、描述与归属" },
  { key: "location", label: "区域与调查", hint: "一级区域、调查点与范围" },
  { key: "flow", label: "任务流转", hint: "后续任务与完成条件" },
];
const treeScroll = ref<HTMLElement>();
const inspector = ref<HTMLElement>();
const childPage = ref(0);
const expanded = ref(new Set<string>(["unassigned"]));
const subPages = ref<Record<number, number>>({});
const treePage = ref(0);
const SUB_PAGE_SIZE = 50;
const TREE_PAGE_SIZE = 200;
const pointProperty: ClipPropertyDefinition = {
  key: "investigationPoint", label: "调查点", type: "vector3", defaultValue: "0,0,0",
  description: "调查点的三维坐标。",
};

const chaptersById = computed(() => new Map(props.project.chapters.map((item) => [item.id, item])));
const mainsById = computed(() => new Map(props.project.mainQuests.map((item) => [item.id, item])));
const subsById = computed(() => new Map(props.project.subQuests.map((item) => [item.id, item])));
const subGroups = computed(() => {
  const groups = new Map<number, QuestSub[]>();
  for (const sub of props.project.subQuests) {
    const group = groups.get(sub.mainQuestId) ?? [];
    group.push(sub);
    groups.set(sub.mainQuestId, group);
  }
  return groups;
});
const referenceOptions = computed<TreeSelectOption[]>(() => {
  const roots: TreeSelectOption[] = props.project.chapters.map(chapter => ({
    key: `chapter:${chapter.id}`, label: `章节 · ${chapter.title || '未命名章节'} #${chapter.id}`, children: [],
  }));
  const chapterOptions = new Map(roots.map(option => [option.key, option]));
  const unassigned: TreeSelectOption = { key: "unassigned", label: "直属主任务", children: [] };
  for (const main of props.project.mainQuests) {
    const chapter = main.chapterId === null ? undefined : chaptersById.value.get(main.chapterId);
    const path = `${chapter?.title ?? '直属主任务'} / ${main.title}`;
    const option: TreeSelectOption = {
      key: `main:${main.id}`, label: `主任务 · ${main.title || '未命名主任务'} #${main.id}`,
      children: (subGroups.value.get(main.id) ?? []).map(sub => ({
        key: sub.id, label: `${sub.title || '未命名子任务'} #${sub.id}`,
        searchText: `${path} / ${sub.title} ${sub.id}`,
      })),
    };
    const parent = chapterOptions.get(`chapter:${main.chapterId}`) ?? unassigned;
    parent.children!.push(option);
  }
  if (unassigned.children!.length) roots.push(unassigned);
  return roots;
});

function chooseNextQuest(value: number | null, index?: number) {
  const sub = selectedSub.value;
  if (!sub || (value !== null && !subsById.value.has(value))) return;
  if (index !== undefined && (index < 0 || index >= sub.nextQuestIds.length)) return;
  if (index !== undefined && sub.nextQuestIds[index] === value) return;
  if (value !== null && sub.nextQuestIds.some((id, position) => id === value && position !== index)) {
    toast.warning("该任务已在后续任务中，不能重复添加");
    return;
  }
  if (index !== undefined) {
    sub.nextQuestIds[index] = value;
  } else if (value !== null && sub.nextQuestIds.length < 100) sub.nextQuestIds.push(value);
}

function chooseFailureQuest(value: number | null) {
  const sub = selectedSub.value;
  if (!sub || sub.failureQuestId !== -1 || value === null || !subsById.value.has(value)) return;
  sub.failureQuestId = value;
}
function removeFailureQuest() {
  if (selectedSub.value) selectedSub.value.failureQuestId = -1;
}

const selectedChapter = computed(() => selection.value?.kind === "chapter"
  ? chaptersById.value.get(selection.value.id) : undefined);
const selectedMain = computed(() => selection.value?.kind === "main"
  ? mainsById.value.get(selection.value.id) : undefined);
const selectedSub = computed(() => selection.value?.kind === "sub"
  ? subsById.value.get(selection.value.id) : undefined);
const selectedItem = computed(() => selectedChapter.value ?? selectedMain.value ?? selectedSub.value);
const selectedParentMain = computed(() => selectedMain.value
  ?? (selectedSub.value ? mainsById.value.get(selectedSub.value.mainQuestId) : undefined));
const selectedKindLabel = computed(() => ({ chapter: "章节", main: "主任务", sub: "子任务" })[selection.value?.kind ?? "chapter"]);
const activeChapterId = computed(() => selectedChapter.value?.id ?? selectedParentMain.value?.chapterId ?? null);
const parentChapter = computed(() => activeChapterId.value === null ? undefined : chaptersById.value.get(activeChapterId.value));
const childItems = computed(() => selectedChapter.value
  ? props.project.mainQuests.filter(main => main.chapterId === selectedChapter.value?.id)
  : selectedMain.value ? subGroups.value.get(selectedMain.value.id) ?? [] : []);
const childPageCount = computed(() => Math.max(1, Math.ceil(childItems.value.length / SUB_PAGE_SIZE)));
const visibleChildren = computed(() => childItems.value.slice(childPage.value * SUB_PAGE_SIZE, (childPage.value + 1) * SUB_PAGE_SIZE));

type TreeRow = {
  key: string;
  kind: "unassigned" | "chapter" | "main" | "sub" | "pagination";
  id: number;
  label: string;
  depth: number;
  count?: number;
  page?: number;
  pageCount?: number;
};

function isOpen(key: string) {
  return Boolean(search.value.trim()) || expanded.value.has(key);
}

function matches(item: { id: number; title: string; description?: string }, query: string) {
  return `${item.id} ${item.title} ${item.description ?? ""}`.toLocaleLowerCase().includes(query);
}

const treeRows = computed<TreeRow[]>(() => {
  const rows: TreeRow[] = [];
  const query = search.value.trim().toLocaleLowerCase();
  const filteredSubs = new Map<number, QuestSub[]>();
  const visibleMains = new Set<number>();
  const mainGroups = new Map<number | null, QuestMain[]>();
  for (const main of props.project.mainQuests) {
    const chapter = main.chapterId === null ? undefined : chaptersById.value.get(main.chapterId);
    const includeAll = !query || matches(main, query) || (chapter && matches(chapter, query));
    const children = subGroups.value.get(main.id) ?? [];
    const matchingChildren = includeAll ? children : children.filter((sub) => matches(sub, query));
    filteredSubs.set(main.id, matchingChildren);
    if (includeAll || matchingChildren.length) visibleMains.add(main.id);
    const chapterKey = chapter?.id ?? null;
    const siblings = mainGroups.get(chapterKey) ?? [];
    siblings.push(main);
    mainGroups.set(chapterKey, siblings);
  }

  function addMains(chapterId: number | null) {
    for (const main of mainGroups.get(chapterId) ?? []) {
      if (!visibleMains.has(main.id)) continue;
      const key = `main:${main.id}`;
      rows.push({ key, kind: "main", id: main.id, label: main.title || "未命名主任务", depth: 1,
        count: subGroups.value.get(main.id)?.length ?? 0 });
      if (!isOpen(key)) continue;
      const children = filteredSubs.get(main.id) ?? [];
      const pageCount = Math.max(1, Math.ceil(children.length / SUB_PAGE_SIZE));
      const page = Math.min(subPages.value[main.id] ?? 0, pageCount - 1);
      for (const sub of children.slice(page * SUB_PAGE_SIZE, (page + 1) * SUB_PAGE_SIZE)) {
        rows.push({ key: `sub:${sub.id}`, kind: "sub", id: sub.id,
          label: sub.title || "未命名子任务", depth: 2 });
      }
      if (pageCount > 1) rows.push({ key: `pages:${main.id}`, kind: "pagination", id: main.id,
        label: main.title, depth: 2, page, pageCount, count: children.length });
    }
  }

  for (const chapter of props.project.chapters) {
    const mains = mainGroups.get(chapter.id) ?? [];
    if (query && !matches(chapter, query) && !mains.some((main) => visibleMains.has(main.id))) continue;
    const key = `chapter:${chapter.id}`;
    rows.push({ key, kind: "chapter", id: chapter.id, label: chapter.title || "未命名章节", depth: 0, count: mains.length });
    if (isOpen(key)) addMains(chapter.id);
  }
  const unassigned = mainGroups.get(null) ?? [];
  if (!query || unassigned.some((main) => visibleMains.has(main.id))) {
    rows.push({ key: "unassigned", kind: "unassigned", id: -1, label: "直属主任务", depth: 0, count: unassigned.length });
    if (isOpen("unassigned")) addMains(null);
  }
  return rows;
});
const treePageCount = computed(() => Math.max(1, Math.ceil(treeRows.value.length / TREE_PAGE_SIZE)));
const visibleRows = computed(() => treeRows.value.slice(treePage.value * TREE_PAGE_SIZE, (treePage.value + 1) * TREE_PAGE_SIZE));

watch(treePageCount, (count) => { treePage.value = Math.min(treePage.value, count - 1); });
watch(search, () => { treePage.value = 0; subPages.value = {}; });
watch(childPageCount, count => { childPage.value = Math.min(childPage.value, count - 1); });
watch(() => selection.value ? `${selection.value.kind}:${selection.value.id}` : "", () => {
  childPage.value = 0;
  inspector.value?.scrollTo({ top: 0 });
});
watch(() => props.project, () => {
  // Undo replaces the document but keeps selection/search on surviving tasks.
  if (selection.value && !selectedItem.value) selection.value = null;
});

function toggle(key: string) {
  if (search.value.trim()) return;
  const updated = new Set(expanded.value);
  if (updated.has(key)) updated.delete(key);
  else updated.add(key);
  expanded.value = updated;
}

function selectRow(row: TreeRow) {
  if (row.kind === "unassigned") {
    selection.value = null;
    toggle(row.key);
  } else if (row.kind !== "pagination") {
    selection.value = { kind: row.kind, id: row.id };
    if (row.kind === "chapter" || row.kind === "main") toggle(row.key);
  }
}

async function reveal(target: QuestSelection) {
  search.value = "";
  selection.value = target;
  // Wait for the search reset before choosing a child page; search clears pagination.
  await nextTick();
  const open = new Set(expanded.value);
  const main = target.kind === "main" ? mainsById.value.get(target.id)
    : target.kind === "sub" ? mainsById.value.get(subsById.value.get(target.id)?.mainQuestId ?? -1) : undefined;
  if (target.kind === "chapter") open.add(`chapter:${target.id}`);
  if (main) {
    open.add(main.chapterId === null ? "unassigned" : `chapter:${main.chapterId}`);
    open.add(`main:${main.id}`);
    if (target.kind === "sub") {
      const index = (subGroups.value.get(main.id) ?? []).findIndex((sub) => sub.id === target.id);
      subPages.value = { ...subPages.value, [main.id]: Math.max(0, Math.floor(index / SUB_PAGE_SIZE)) };
    }
  }
  expanded.value = open;
  const index = treeRows.value.findIndex((row) => row.key === `${target.kind}:${target.id}`);
  treePage.value = Math.max(0, Math.floor(index / TREE_PAGE_SIZE));
}

async function locateSelection() {
  if (!selection.value) return;
  await reveal(selection.value);
  await nextTick();
  treeScroll.value?.querySelector(".tree-row.selected")?.scrollIntoView({ block: "nearest" });
}

function expandAll() {
  expanded.value = new Set(["unassigned", ...props.project.chapters.map(item => `chapter:${item.id}`),
    ...props.project.mainQuests.map(item => `main:${item.id}`)]);
}

function reportError(error: unknown) {
  toast.error(error instanceof Error ? error.message : String(error));
}

function addChapter() {
  try { void reveal({ kind: "chapter", id: createQuestChapter(props.project).id }); }
  catch (error) { reportError(error); }
}

function addMain(chapterId: number | null = activeChapterId.value) {
  try { void reveal({ kind: "main", id: createQuestMain(props.project, chapterId).id }); }
  catch (error) { reportError(error); }
}

function addSub(mainId = selectedParentMain.value?.id) {
  if (mainId === undefined) { toast.warning("请先选择一个主任务，再添加子任务"); return; }
  try { void reveal({ kind: "sub", id: createQuestSub(props.project, mainId).id }); }
  catch (error) { reportError(error); }
}

function changeChapter(event: Event) {
  if (!selectedMain.value) return;
  const raw = (event.target as HTMLSelectElement).value;
  const chapterId = raw === "" ? null : Number(raw);
  if (chapterId !== null && !chaptersById.value.has(chapterId)) return;
  selectedMain.value.chapterId = chapterId;
  void reveal({ kind: "main", id: selectedMain.value.id });
}

function changeMain(event: Event) {
  if (!selectedSub.value) return;
  const mainId = Number((event.target as HTMLSelectElement).value);
  if (!mainsById.value.has(mainId)) return;
  selectedSub.value.mainQuestId = mainId;
  void reveal({ kind: "sub", id: selectedSub.value.id });
}

function updatePoint(value: unknown) {
  if (selectedSub.value && typeof value === "string") {
    selectedSub.value.investigationPoint = value;
  }
}

function updateRange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (selectedSub.value && Number.isFinite(input.valueAsNumber)) {
    selectedSub.value.investigationRange = input.valueAsNumber;
  }
}

function updateSubInteger(key: "failureQuestId" | "questProgress" | "belondPrimaryId", event: Event, commit = false) {
  const sub = selectedSub.value;
  if (!sub) return;
  const input = event.target as HTMLInputElement;
  if (key === "failureQuestId" && input.value.trim() === "" && !input.validity?.badInput) {
    sub.failureQuestId = null;
    return;
  }
  const value = input.valueAsNumber;
  if (!Number.isInteger(value) || value < -2147483648 || value > 2147483647) {
    if (commit) {
      toast.warning(`${key === "failureQuestId" ? "失败回溯任务 ID" : key === "belondPrimaryId" ? "所属一级区域 ID" : "任务进度"}必须是 Int32 整数`);
      input.value = String(sub[key] ?? "");
    }
    return;
  }
  sub[key] = value;
}

function moveNextQuest(index: number, offset: number) {
  const ids = selectedSub.value?.nextQuestIds;
  const destination = index + offset;
  if (!ids || index < 0 || index >= ids.length || destination < 0 || destination >= ids.length) return;
  [ids[index], ids[destination]] = [ids[destination], ids[index]];
}

function removeNextQuest(index: number) {
  const ids = selectedSub.value?.nextQuestIds;
  if (ids && index >= 0 && index < ids.length) ids.splice(index, 1);
}

function removeSelected() {
  const target = selection.value;
  const item = selectedItem.value;
  if (!target || !item) return;
  let clearedReferenceCount = 0;
  if (target.kind === "chapter") {
    const count = props.project.mainQuests.filter((main) => main.chapterId === target.id).length;
    if (!confirm(`删除章节「${item.title}」？\n其下 ${count} 个主任务将移到“直属主任务”，主任务和子任务不会删除。`)) return;
    for (const main of props.project.mainQuests) if (main.chapterId === target.id) main.chapterId = null;
    props.project.chapters = props.project.chapters.filter((chapter) => chapter.id !== target.id);
    expanded.value = new Set([...expanded.value, "unassigned"]);
    selection.value = null;
  } else if (target.kind === "main") {
    const count = subGroups.value.get(target.id)?.length ?? 0;
    if (!confirm(`删除主任务「${item.title}」及其 ${count} 个子任务？\n此操作会同时移除这些任务的数据，其他子任务指向它们的后续任务和失败回溯引用将置空，后续任务列表保留位置。`)) return;
    const removedIds = new Set(props.project.subQuests.filter((sub) => sub.mainQuestId === target.id).map((sub) => sub.id));
    clearedReferenceCount = removeQuestSubQuests(props.project, removedIds).clearedReferenceCount;
    props.project.mainQuests = props.project.mainQuests.filter((main) => main.id !== target.id);
    selection.value = null;
  } else {
    if (!confirm(`删除子任务「${item.title}」（ID ${target.id}）？\n其他子任务指向它的后续任务和失败回溯引用将置空，后续任务列表保留位置。`)) return;
    const parentId = selectedSub.value?.mainQuestId;
    clearedReferenceCount = removeQuestSubQuests(props.project, new Set([target.id])).clearedReferenceCount;
    selection.value = parentId === undefined ? null : { kind: "main", id: parentId };
  }
  toast.success(`已删除${({ chapter: "章节", main: "主任务", sub: "子任务" })[target.kind]}${clearedReferenceCount ? `，已将 ${clearedReferenceCount} 项任务引用置空` : ""}`);
}

function changeSubPage(row: TreeRow, offset: number) {
  const page = Math.min((row.pageCount ?? 1) - 1, Math.max(0, (row.page ?? 0) + offset));
  subPages.value = { ...subPages.value, [row.id]: page };
}

function parentLabel(main: QuestMain) {
  const chapter = main.chapterId === null ? undefined : chaptersById.value.get(main.chapterId);
  return `${chapter?.title || "直属主任务"} / ${main.title || "未命名主任务"} #${main.id}`;
}

function editFlowTask(id: number) {
  editorView.value = "properties";
  inspectorSection.value = "flow";
  void reveal({ kind: "sub", id });
}
</script>

<template>
  <section class="quest-panel" aria-label="任务层级编辑器">
    <header class="quest-toolbar">
      <div>
        <strong>任务编排</strong>
        <span class="quest-counts">章节 {{ project.chapters.length }}/100 · 主任务 {{ project.mainQuests.length }}/100 · 子任务 {{ project.subQuests.length }}/10000</span>
      </div>
      <div class="toolbar-actions">
        <div class="quest-view-switch" role="group" aria-label="任务编辑视图">
          <button type="button" :aria-pressed="editorView === 'properties'" @click="editorView = 'properties'">任务属性</button>
          <button type="button" :aria-pressed="editorView === 'flow'" @click="editorView = 'flow'">任务衔接图</button>
        </div>
        <button type="button" :disabled="project.chapters.length >= 100" @click="addChapter">＋ 章节</button>
        <button type="button" :disabled="project.mainQuests.length >= 100" :title="activeChapterId === null ? '创建直属主任务' : '在当前章节创建主任务'" @click="addMain()">＋ 主任务</button>
        <button type="button" class="primary" :disabled="!selectedParentMain || project.subQuests.length >= 10000" @click="addSub()">＋ 子任务</button>
      </div>
    </header>

    <div class="quest-columns">
      <StudioSidebarContent><aside class="quest-browser" aria-label="任务层级" :inert="sidebarDisabled">
        <div class="browser-heading"><strong>任务目录</strong><span>{{ project.mainQuests.length + project.subQuests.length }} 项任务</span></div>
        <div class="tree-search">
          <input v-model="search" type="search" aria-label="搜索任务" placeholder="搜索标题、描述或 ID" />
          <small v-if="search.trim()">搜索结果会展开匹配任务的父级</small>
        </div>
        <div class="tree-tools">
          <button type="button" :disabled="Boolean(search.trim())" @click="expandAll">全部展开</button>
          <button type="button" :disabled="Boolean(search.trim())" @click="expanded = new Set(); treePage = 0">收起</button>
          <button type="button" :disabled="!selection" @click="locateSelection">定位当前</button>
        </div>
        <div ref="treeScroll" class="tree-scroll">
          <p v-if="!visibleRows.length" class="tree-empty">没有匹配的章节或任务</p>
          <div v-for="row in visibleRows" :key="row.key" class="tree-row" :class="[row.kind, { selected: selection?.kind === row.kind && selection?.id === row.id }]" :style="{ '--tree-depth': row.depth }">
            <template v-if="row.kind === 'pagination'">
              <div class="sub-pagination">
                <button type="button" :disabled="row.page === 0" :aria-label="`${row.label} 上一页子任务`" @click="changeSubPage(row, -1)">‹</button>
                <span>{{ (row.page ?? 0) + 1 }}/{{ row.pageCount }} 页 · {{ row.count }} 项</span>
                <button type="button" :disabled="(row.page ?? 0) + 1 >= (row.pageCount ?? 1)" :aria-label="`${row.label} 下一页子任务`" @click="changeSubPage(row, 1)">›</button>
              </div>
            </template>
            <template v-else>
              <button v-if="row.kind !== 'sub'" type="button" class="fold-button" :aria-label="`${isOpen(row.key) ? '折叠' : '展开'}${row.label}`" :aria-expanded="isOpen(row.key)" :disabled="Boolean(search.trim())" @click="toggle(row.key)">{{ isOpen(row.key) ? '▾' : '▸' }}</button>
              <span v-else class="sub-dot">·</span>
              <button type="button" class="tree-item" :title="`${row.label}${row.id >= 0 ? ` · ID ${row.id}` : ''}`" :aria-current="selection?.kind === row.kind && selection?.id === row.id ? 'true' : undefined" :aria-expanded="row.kind !== 'sub' ? isOpen(row.key) : undefined" @click="selectRow(row)">
                <span class="tree-kind">{{ row.kind === 'chapter' ? '章' : row.kind === 'sub' ? '子' : '主' }}</span>
                <span class="tree-title">{{ row.label }}</span>
                <small v-if="row.id >= 0">#{{ row.id }}</small>
                <span v-if="row.count !== undefined" class="tree-count">{{ row.count }}</span>
              </button>
              <button v-if="row.kind !== 'sub'" type="button" class="tree-add" :disabled="row.kind === 'main' ? project.subQuests.length >= 10000 : project.mainQuests.length >= 100" :aria-label="`在${row.label}下添加${row.kind === 'main' ? '子任务' : '主任务'}`" :title="row.kind === 'main' ? '添加子任务' : '添加主任务'" @click="row.kind === 'main' ? addSub(row.id) : addMain(row.kind === 'chapter' ? row.id : null)">＋</button>
            </template>
          </div>
          <div v-if="!project.chapters.length && !project.mainQuests.length && !search.trim()" class="tree-empty">
            <p>先创建章节，或直接创建主任务。</p>
            <button type="button" @click="addMain(null)">＋ 创建主任务</button>
          </div>
        </div>
        <footer v-if="treePageCount > 1" class="tree-pagination">
          <button type="button" :disabled="treePage === 0" @click="treePage--">上一页</button>
          <span>{{ treePage + 1 }} / {{ treePageCount }}</span>
          <button type="button" :disabled="treePage + 1 >= treePageCount" @click="treePage++">下一页</button>
        </footer>
        <p class="tree-footnote">点击条目编辑 · 点击 ＋ 添加下级任务<br />改名或移动归属时，任务 ID 保持不变。</p>
      </aside></StudioSidebarContent>

      <KeepAlive><QuestFlowGraph v-if="editorView === 'flow'" :project="project" :selection="selection" @select="reveal({ kind: 'sub', id: $event })" @edit="editFlowTask" /></KeepAlive>
      <main v-show="editorView === 'properties'" ref="inspector" class="quest-inspector" aria-label="任务属性">
        <template v-if="selectedItem">
          <nav class="quest-breadcrumb" aria-label="任务路径">
            <span>任务编排</span>
            <template v-if="parentChapter && !selectedChapter"><span aria-hidden="true">/</span><button type="button" @click="reveal({ kind: 'chapter', id: parentChapter.id })">{{ parentChapter.title || '未命名章节' }}</button></template>
            <template v-if="selectedSub && selectedParentMain"><span aria-hidden="true">/</span><button type="button" @click="reveal({ kind: 'main', id: selectedParentMain.id })">{{ selectedParentMain.title || '未命名主任务' }}</button></template>
            <span aria-hidden="true">/</span><span>{{ selectedKindLabel }}</span>
          </nav>
          <header class="inspector-header">
            <div><span class="kind-badge">{{ selectedKindLabel }}</span><h2>{{ selectedItem.title || `未命名${selectedKindLabel}` }}</h2></div>
            <button type="button" class="danger" @click="removeSelected">删除{{ selectedKindLabel }}</button>
          </header>
          <div class="identity-strip">
            <span>ID <code>{{ selectedItem.id }}</code></span>
            <span v-if="selectedSub">归属主任务 <code>{{ selectedParentMain?.title || '未命名主任务' }}</code></span>
            <span v-else>只读 · 移动时保持不变</span>
          </div>
          <nav v-if="selectedSub" class="inspector-tabs" aria-label="子任务属性分组">
            <button v-for="tab in inspectorSections" :key="tab.key" type="button" :class="{ active: inspectorSection === tab.key }" :aria-pressed="inspectorSection === tab.key" @click="inspectorSection = tab.key"><strong>{{ tab.label }}</strong><small>{{ tab.hint }}</small></button>
          </nav>
          <div v-show="!selectedSub || inspectorSection === 'basic'" class="form-card title-card">
          <label class="quest-field"><span>{{ selectedKindLabel }}标题 <code>title</code></span><input v-model="selectedItem.title" :aria-label="`${selectedKindLabel}标题`" placeholder="填写标题" /></label>
          </div>

          <template v-if="selectedChapter">
            <p class="form-description">章节用于组织主任务。删除章节时，下属任务会移到直属主任务。</p>
          </template>
          <template v-else-if="selectedMain">
            <label class="quest-field"><span>归属章节 <code>chapter</code></span><StudioSelectField :value="selectedMain.chapterId ?? ''" aria-label="归属章节" @change="changeChapter"><option value="">无章节 · 直属主任务</option><option v-for="chapter in project.chapters" :key="chapter.id" :value="chapter.id">{{ chapter.title || '未命名章节' }} #{{ chapter.id }}</option></StudioSelectField></label>
            <label class="quest-field"><span>主任务样式 <code>style</code></span><StudioSelectField v-model="selectedMain.style" aria-label="选择主任务样式预设"><option v-if="!questStyleOptions.some(item => item.value === selectedMain?.style)" :value="selectedMain.style">{{ selectedMain.style || '未设置' }}（当前值）</option><option v-for="item in questStyleOptions" :key="item.value" :value="item.value">{{ item.label }}（{{ item.value }}）</option></StudioSelectField><small>从下拉框选择主任务样式。</small><small v-if="questStyleError" role="alert">{{ questStyleError }} <button type="button" @click="retryQuestStyles().catch(() => undefined)">重试样式预设</button></small></label>

          </template>
          <template v-else-if="selectedSub">
            <section v-show="inspectorSection === 'basic'" class="form-card" aria-label="基本信息">
            <label class="quest-field"><span>归属主任务 <code>mainQuestId</code></span><StudioSelectField :value="selectedSub.mainQuestId" aria-label="归属主任务" @change="changeMain"><option v-for="main in project.mainQuests" :key="main.id" :value="main.id">{{ parentLabel(main) }}</option></StudioSelectField></label>
            <label class="quest-field"><span>任务描述 <code>desc</code></span><textarea v-model="selectedSub.description" aria-label="任务描述" rows="4" placeholder="填写任务描述" /></label>
            <label class="hidden-field"><input v-model="selectedSub.hidden" type="checkbox" role="switch" aria-label="隐藏任务" /><span>隐藏任务</span></label>
            </section>
            <section v-show="inspectorSection === 'location'" class="form-card location-card" aria-label="区域与调查">
            <h3 class="form-heading">区域与调查</h3><p class="form-description">设置任务所属的一级区域，以及玩家需要调查的位置。</p>
            <label class="quest-field"><span>单位状态 <code>unitState · ConfigReference</code></span><input v-model="selectedSub.unitState" aria-label="单位状态" placeholder="填写配置引用" /></label>
            <div class="quest-field"><span>所属一级区域 <code>belondPrimaryId</code></span><QuestMainAreaSelect v-model="selectedSub.belondPrimaryId" /></div>
            <label class="hidden-field"><input v-model="selectedSub.showGuide" type="checkbox" role="switch" aria-label="显示任务地图指引" /><span>显示任务地图指引</span></label>
            <label class="hidden-field"><input v-model="selectedSub.showEffect" type="checkbox" role="switch" aria-label="显示任务指引特效" /><span>显示任务指引特效</span></label>
            <p v-if="selectedSub.legacyInvestigationPoint" class="inspector-info">已沿用旧调查点的 Vector3 坐标；原配置已保留备份。若原先使用实体、GUID 或偏移，请核对这里的最终坐标。</p>
            <div class="position-editor"><ClipPropertyEditor :property="pointProperty" :model-value="selectedSub.investigationPoint" @update:model-value="updatePoint" /></div>
            <label class="quest-field"><span>调查范围 <code>investigationRange</code></span><input type="number" aria-label="调查范围" :value="selectedSub.investigationRange" step="any" @input="updateRange" /><small>保留结构体默认值 -1；可填写所需范围。</small></label>
            </section>
            <div v-show="inspectorSection === 'flow'" class="flow-section">
            <section class="next-quests" aria-label="后续任务">
              <header><h3>后续任务 <code>nextQuestIds · Int32List</code></h3><small>{{ selectedSub.nextQuestIds.length }}/100</small></header>
              <p>当前任务结束后，按以下顺序衔接后续任务。</p>
              <div v-if="!selectedSub.nextQuestIds.length" class="flow-empty">暂无后续任务，从下方选择任务以建立关联。</div>
              <ol v-if="selectedSub.nextQuestIds.length">
                <li v-for="(id, index) in selectedSub.nextQuestIds" :key="index" :class="{ 'is-empty': id === null }">
                  <span class="next-quest-order">{{ index + 1 }}</span>
                  <QuestReferenceSelect :options="referenceOptions" :model-value="id" :label="`选择后续任务 ${index + 1}`" @update:model-value="chooseNextQuest($event, index)" />
                  <small v-if="id === null">空引用 · 导出为 -1</small>
                  <small v-else :title="subsById.get(id)?.title">{{ subsById.has(id) ? (subsById.get(id)?.title || '未命名子任务') : '当前文件未找到此 ID' }}</small>
                  <button type="button" :disabled="index === 0" :aria-label="`上移后续任务 ${index + 1}`" @click="moveNextQuest(index, -1)">↑</button>
                  <button type="button" :disabled="index === selectedSub.nextQuestIds.length - 1" :aria-label="`下移后续任务 ${index + 1}`" @click="moveNextQuest(index, 1)">↓</button>
                  <button type="button" :aria-label="`移除后续任务 ${index + 1}`" @click="removeNextQuest(index)">×</button>
                </li>
              </ol>
              <div class="next-quest-add">
                <QuestReferenceSelect :options="referenceOptions" :model-value="null" label="添加后续任务" placeholder="＋ 选择后续任务，可搜索任务名" :disabled="selectedSub.nextQuestIds.length >= 100" @update:model-value="chooseNextQuest($event)" />
              </div>
              <small>删除任务时，指向它的引用会置空并保留位置；可以重新选择任务或移除此项。导出时会警告空引用及当前文件中不存在的 ID。</small>
            </section>
            <section class="form-card" aria-label="完成与回溯"><h3 class="form-heading">完成与回溯</h3>
            <div class="quest-field"><span>失败回溯任务 <code>失败回溯任务 · Int32</code></span>
              <QuestReferenceSelect v-if="selectedSub.failureQuestId === -1" :options="referenceOptions" :model-value="null" label="添加回溯任务" placeholder="＋ 添加回溯任务，可搜索任务名" @update:model-value="chooseFailureQuest" />
              <div v-else class="failure-quest-row">
                <div class="failure-quest-name">
                  <strong v-if="selectedSub.failureQuestId === null">回溯目标已删除或未指定</strong>
                  <template v-else><strong>{{ subsById.get(selectedSub.failureQuestId)?.title || (subsById.has(selectedSub.failureQuestId) ? '未命名子任务' : '未找到回溯任务') }}</strong><small>#{{ selectedSub.failureQuestId }}</small></template>
                </div>
                <button type="button" class="danger" aria-label="删除回溯任务关联" @click="removeFailureQuest">删除</button>
              </div>
              <small v-if="selectedSub.failureQuestId === null || (selectedSub.failureQuestId !== -1 && !subsById.has(selectedSub.failureQuestId))">此关联未找到有效目标，可删除关联后重新选择。</small>
              <small v-else>只能添加一个回溯任务；删除关联后可以重新选择。</small>
            </div>
            <label class="hidden-field"><input v-model="selectedSub.finishMainQuest" type="checkbox" role="switch" aria-label="完成主任务" /><span>完成主任务</span></label>
            <label class="quest-field"><span>任务进度 <code>questProgress · Int32</code></span><input type="number" aria-label="任务进度" :value="selectedSub.questProgress" step="1" min="-2147483648" max="2147483647" @input="updateSubInteger('questProgress', $event)" @change="updateSubInteger('questProgress', $event, true)" /><small>默认 0，按填写的整数导出。</small></label>
            </section>
            </div>
          </template>
          <section v-if="selectedChapter || selectedMain" class="children-card">
            <header><div><h3>{{ selectedChapter ? '章节下的主任务' : '主任务下的子任务' }}</h3><small>{{ childItems.length }} 项 · 点击直接编辑</small></div><button type="button" class="primary" :disabled="selectedChapter ? project.mainQuests.length >= 100 : project.subQuests.length >= 10000" @click="selectedChapter ? addMain(selectedChapter.id) : addSub(selectedMain?.id)">＋ {{ selectedChapter ? '主任务' : '子任务' }}</button></header>
            <p v-if="!childItems.length" class="form-description">还没有{{ selectedChapter ? '主任务' : '子任务' }}，创建一个开始编排。</p>
            <button v-for="child in visibleChildren" :key="child.id" type="button" class="child-link" @click="reveal({ kind: selectedChapter ? 'main' : 'sub', id: child.id })"><span class="child-symbol">{{ selectedChapter ? '主' : '子' }}</span><span>{{ child.title || '未命名任务' }}</span><small>#{{ child.id }}</small><span aria-hidden="true">›</span></button>
            <footer v-if="childPageCount > 1" class="tree-pagination"><button type="button" :disabled="childPage === 0" @click="childPage--">上一页</button><span>{{ childPage + 1 }} / {{ childPageCount }}</span><button type="button" :disabled="childPage + 1 >= childPageCount" @click="childPage++">下一页</button></footer>
          </section>
        </template>
        <div v-else class="inspector-empty">
          <span class="empty-symbol">☷</span><h2>按层级组织任务</h2><p>章节 → 主任务 → 子任务<br />也可以跳过章节，直接创建主任务。</p>
          <div><button type="button" :disabled="project.chapters.length >= 100" @click="addChapter">＋ 创建章节</button><button type="button" class="primary" :disabled="project.mainQuests.length >= 100" @click="addMain(null)">＋ 创建主任务</button></div>
          <small>选择左侧条目编辑属性。任务配置自动保存在当前工作区，结构体导出用于千星变量。</small>
        </div>
      </main>
    </div>
  </section>
</template>

<style scoped>
.quest-panel { display: flex; flex-direction: column; height: 100%; min-height: 0; min-width: 0; color: #334155; background: #f4f6fa; font-size: 13px; text-align: left; container-type: inline-size; }
button, input, textarea, select { font: inherit; }
button { border: 1px solid #cbd5e1; border-radius: 6px; background: #fff; color: #334155; cursor: pointer; padding: 6px 10px; line-height: 1.4; }
button:hover:not(:disabled) { background: #eff6ff; border-color: #93b4e1; }
button:focus-visible, input:focus-visible, textarea:focus-visible, select:focus-visible { outline: 2px solid #60a5fa; outline-offset: 1px; }
button:disabled { opacity: .45; cursor: default; }
button.primary { background: #2563eb; border-color: #2563eb; color: white; }
button.primary:hover:not(:disabled) { background: #1d4ed8; }
button.danger { color: #b91c1c; border-color: #fecaca; white-space: nowrap; }
button.danger:hover { background: #fef2f2; }
.quest-toolbar { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px; padding: 10px 16px; border-bottom: 1px solid #dbe3ed; background: #fff; }
.quest-toolbar > div:first-child { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px 16px; }
.quest-toolbar strong { font-size: 15px; color: #1e293b; }
.quest-counts { font-size: 11px; color: #64748b; }
.toolbar-actions { display: flex; flex-wrap: wrap; gap: 6px; }
.quest-view-switch { display: flex; padding: 2px; margin-right: 8px; border: 1px solid #d7e1f2; border-radius: 7px; background: #f1f5fc; }
.quest-view-switch button { border: 0; background: transparent; }
.quest-view-switch button[aria-pressed="true"] { background: #fff; color: #167dde; box-shadow: 0 1px 4px #36598715; }
.quest-columns { display: grid; grid-template-columns: clamp(245px, 28%, 340px) minmax(0, 1fr); flex: 1; min-height: 0; }
.quest-browser { display: flex; flex-direction: column; min-width: 0; min-height: 0; background: #fff; border-right: 1px solid #dbe3ed; }
.tree-search { padding: 12px; border-bottom: 1px solid #eef2f7; }
.tree-search input, .quest-field > input, .quest-field > textarea, .quest-field > select { box-sizing: border-box; width: 100%; min-width: 0; border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px 10px; background: #fff; color: #1e293b; }
.tree-search small { display: block; color: #64748b; margin-top: 6px; font-size: 11px; }
.tree-scroll { flex: 1; min-height: 0; overflow: auto; padding: 6px; }
.tree-row { display: flex; align-items: center; min-height: 40px; min-width: 0; padding-left: calc(var(--tree-depth) * 15px); border-radius: 6px; margin: 2px 0; }
.tree-row.selected { background: #eaf0ff; box-shadow: inset 3px 0 #5875d9; }
.tree-row.chapter, .tree-row.unassigned { margin-top: 7px; }
.tree-row:hover { background: #f1f5f9; }
.tree-row.selected:hover { background: #e0ebfe; }
.tree-row button { border: none; background: transparent; border-radius: 4px; }
.fold-button, .sub-dot { flex: 0 0 24px; text-align: center; color: #64748b; }
.fold-button { padding: 5px 0; }
.sub-dot { font-size: 20px; }
.tree-item { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; text-align: left; padding: 6px 2px; }
.tree-kind { flex-shrink: 0; color: #5273a0; font-size: 10px; padding: 1px 4px; background: #e8eef8; border-radius: 3px; }
.chapter .tree-kind { color: #8264a3; background: #f1ebf7; }
.sub .tree-kind { color: #4c856d; background: #e8f3ec; }
.tree-title { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.tree-item small { color: #94a3b8; font-family: inherit; font-size: 10px; }
.tree-count { font-size: 10px; color: #64748b; padding: 1px 4px; background: #f1f5f9; border-radius: 8px; }
.tree-add { flex: 0 0 28px; padding: 6px 0; color: #5273a0; }
.tree-empty { padding: 14px 8px; color: #94a3b8; font-size: 12px; line-height: 1.7; }
.tree-empty p { margin: 0 0 10px; }
.sub-pagination { display: flex; align-items: center; justify-content: center; flex: 1; gap: 7px; font-size: 10px; color: #64748b; }
.sub-pagination button { padding: 3px 10px; border: 1px solid #e2e8f0; }
.tree-pagination { display: flex; align-items: center; justify-content: space-between; gap: 5px; padding: 8px 12px; border-top: 1px solid #e2e8f0; font-size: 11px; }
.tree-footnote { margin: 0; padding: 10px 12px; font-size: 10px; line-height: 1.7; color: #94a3b8; border-top: 1px solid #eef2f7; }
.quest-inspector { overflow: auto; min-height: 0; min-width: 0; padding: 24px clamp(18px, 3%, 44px) 40px; scrollbar-gutter: stable; }
.inspector-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.inspector-header > div { min-width: 0; }
.kind-badge { display: inline-block; font-size: 10px; color: #5273a0; background: #e8eef8; border-radius: 4px; padding: 3px 6px; }
.inspector-header h2 { margin: 8px 0 12px; color: #1e293b; font-size: 20px; line-height: 1.4; overflow-wrap: anywhere; }
.identity-strip { display: flex; flex-wrap: wrap; gap: 8px 20px; font-size: 11px; color: #64748b; padding: 0 0 12px; border-bottom: 1px solid #e0e6ef; margin-bottom: 14px; }
code { color: #7a8ca2; font-family: inherit; font-size: 11px; }
.identity-strip code { color: #334155; margin-left: 4px; }
.quest-field { display: flex; flex-direction: column; gap: 7px; margin-top: 17px; }
.quest-field > span { color: #475569; font-size: 12px; }
.quest-field code, .hidden-field code { margin-left: 5px; }
.quest-field > span > code, .next-quests h3 > code { display: none; }
.quest-field > textarea { min-height: 90px; resize: vertical; line-height: 1.6; }
.quest-field small { font-size: 11px; color: #64748b; line-height: 1.6; }
.position-editor { margin-top: 20px; --timeline-text: #1e293b; --timeline-muted: #334155; --timeline-subtle: #64748b; --timeline-field: #fff; --timeline-border: #cbd5e1; --timeline-accent: #93b4e1; }
.position-editor :deep(.clip-property) { font-size: 12px; }
.position-editor :deep(code), .position-editor :deep(.field-description) { font-size: 10px; }
.position-editor :deep(input:not([type='checkbox'])), .position-editor :deep(select) { padding: 8px; }
.hidden-field { display: flex; flex-direction: row; align-items: center; justify-content: flex-start; width: fit-content; gap: 10px; margin: 18px 0 0; padding: 4px 0; min-height: 24px; text-align: left; cursor: pointer; }
.hidden-field > span { flex: 0 0 auto; color: #334155; font-size: 13px; line-height: 20px; }
.hidden-field input { appearance: none; box-sizing: border-box; position: relative; flex: 0 0 36px; width: 36px; height: 20px; margin: 0; padding: 0; border: 1px solid #94a3b8; border-radius: 999px; background: #cbd5e1; cursor: pointer; transition: background .15s, border-color .15s; }
.hidden-field input::before { content: ''; position: absolute; top: 2px; left: 2px; width: 14px; height: 14px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px #0002; transition: transform .15s; }
.hidden-field input:checked { background: #2563eb; border-color: #2563eb; }
.hidden-field input:checked::before { transform: translateX(16px); }
.hidden-field input:focus-visible { outline: 2px solid #2563eb; outline-offset: 3px; }
@media (prefers-reduced-motion: reduce) { .hidden-field input, .hidden-field input::before { transition: none; } }
.next-quests { margin-top: 0; padding: 20px; border: 1px solid #dbe3ed; border-radius: 12px; background: #fff; }
.next-quests header { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.next-quests h3 { margin: 0; font-size: 13px; }
.next-quests p, .next-quests small { color: #7b8ba1; font-size: 11px; line-height: 1.7; }
.next-quests ol { list-style: none; padding: 0; margin: 12px 0; }
.next-quests li { display: flex; align-items: center; gap: 8px; margin-top: 10px; padding: 12px; border: 1px solid #e3e9f2; border-radius: 8px; background: #f8fafc; flex-wrap: wrap; }
.next-quest-order { width: 18px; color: #94a3b8; font-size: 11px; }
.next-quests li small { flex: 1; min-width: 60px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.next-quests li.is-empty small { color: #a16b19; }
.next-quests li button { padding: 5px 7px; }
.next-quest-add { display: flex; gap: 8px; margin: 10px 0; }
.failure-quest-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px; border: 1px solid #dce5f2; border-radius: 8px; background: #f8faff; }
.failure-quest-name { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px; min-width: 0; }
.failure-quest-name strong { overflow-wrap: anywhere; color: #465d80; font-size: 13px; font-weight: 500; }
.failure-quest-row button { flex-shrink: 0; }
.inspector-note { color: #94a3b8; line-height: 1.7; font-size: 11px; margin-top: 20px; }
.inspector-info { border: 1px solid #dbe3ed; background: #fff; padding: 16px; border-radius: 8px; margin-top: 24px; }
.inspector-info h3 { margin: 0; font-size: 13px; color: #475569; }
.inspector-info p { font-size: 12px; line-height: 1.8; color: #64748b; margin: 10px 0 16px; }
.inspector-empty { max-width: 420px; margin: 50px auto; text-align: center; }
.empty-symbol { display: block; color: #b0c4df; font-size: 42px; }
.inspector-empty h2 { color: #475569; font-size: 18px; margin: 12px 0; }
.inspector-empty p { font-size: 13px; line-height: 1.9; color: #64748b; }
.inspector-empty > div { display: flex; justify-content: center; flex-wrap: wrap; gap: 8px; margin: 22px 0; }
.inspector-empty small { display: block; font-size: 11px; color: #94a3b8; line-height: 1.8; }
.browser-heading { display: flex; align-items: center; justify-content: space-between; padding: 18px 16px 0; }
.browser-heading strong { color: #34445b; font-size: 13px; }
.browser-heading > span { color: #7b879a; font-size: 11px; }
.tree-search { border-bottom: 0; padding-bottom: 6px; }
.tree-tools { display: flex; gap: 3px; padding: 0 10px 10px; border-bottom: 1px solid #edf0f5; }
.tree-tools button { font-size: 11px; border: 0; padding: 5px 7px; color: #64748b; background: transparent; }
.tree-tools button:last-child { margin-left: auto; }
.quest-breadcrumb { padding: 0; display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; font-size: 11px; color: #728198; }
.quest-breadcrumb button { padding: 0; border: 0; background: transparent; color: #526bb5; max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.inspector-tabs { position: sticky; top: -1px; z-index: 2; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; margin-bottom: 18px; padding: 5px; border: 1px solid #e1e6ef; border-radius: 12px; background: #eaf0f6; }
.inspector-tabs button { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; padding: 11px 14px; border: 1px solid transparent; background: transparent; border-radius: 8px; text-align: left; }
.inspector-tabs strong { font-size: 13px; font-weight: 600; }
.inspector-tabs small { color: #748197; font-size: 11px; }
.inspector-tabs button.active { background: #fff; border-color: #d9e1f0; color: #385abc; box-shadow: 0 2px 4px #243c6410; }
.form-card { padding: 20px; margin-bottom: 16px; border: 1px solid #e0e6ef; border-radius: 12px; background: #fff; }
.form-card > .quest-field:first-child { margin-top: 0; }
.location-card { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 18px; }
.location-card > .form-heading, .location-card > .form-description, .location-card > .position-editor, .location-card > .inspector-info { grid-column: 1 / -1; }
.form-heading { margin: 0; color: #334155; font-size: 14px; }
.form-description { color: #718097; font-size: 12px; line-height: 1.8; margin: 8px 0 16px; }
.title-card input { font-size: 15px; font-weight: 500; }
.flow-section > .form-card { margin-top: 16px; }
.flow-empty { border: 1px dashed #d5dfea; border-radius: 8px; padding: 20px 12px; margin: 14px 0; text-align: center; color: #78869a; font-size: 12px; }
.children-card { margin-top: 24px; padding: 20px; border: 1px solid #e0e6ef; border-radius: 12px; background: #fff; }
.children-card > header { display: flex; gap: 12px; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.children-card h3 { font-size: 14px; margin: 0 0 6px; }
.children-card small { color: #7b879a; font-size: 11px; }
.child-link { display: flex; align-items: center; gap: 12px; width: 100%; margin-top: 6px; padding: 12px; border-color: #e8edf4; text-align: left; }
.child-link > span:nth-child(2) { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.child-symbol { display: grid; place-items: center; flex: 0 0 28px; height: 28px; border-radius: 8px; background: #eff3fc; color: #5973aa; font-size: 11px; }
@container (max-width: 760px) {
  .quest-columns { grid-template-columns: 220px minmax(0, 1fr); }
  .quest-inspector { padding: 16px; }
  .inspector-header { flex-wrap: wrap; }
  .inspector-tabs button { padding: 10px 5px; align-items: center; }
  .inspector-tabs small { display: none; }
  .form-card, .next-quests, .children-card { padding: 14px; }
  .tree-row { padding-left: calc(var(--tree-depth) * 10px); }
}
@container (max-width: 560px) {
  .quest-columns { display: flex; flex-direction: column; overflow: auto; }
  .quest-browser { flex: 0 0 230px; border-right: 0; border-bottom: 1px solid #dbe3ed; }
  .quest-inspector { flex: 1 0 auto; overflow: visible; }
  .tree-footnote { display: none; }
  .location-card { grid-template-columns: minmax(0, 1fr); }
  .quest-breadcrumb { margin-bottom: 12px; }
}
</style>
